import React, {useState, useEffect} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { friendlyAuthError } from "../utils/authErrors";
import sigmaLogo from "../Images/sigma-bank-logo.png";

function AdminSignIn() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const navigate = useNavigate();
    const { signIn, signOut, user } = useAuth();

    // Redirect if already logged in (not during active sign-in)
    useEffect(() => {
        if (user && !loading) {
            const role = user.user_metadata?.role;
            if (String(role) === '4') {
                navigate('/admin');
            }
        }
    }, [user, loading, navigate]);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!email || !password) {
            setError('Please enter both email and password to continue.');
            setLoading(false);
            return;
        }

        const { user: signedInUser, error: signInError } = await signIn(email, password);

        if (signInError) {
            setError(friendlyAuthError(signInError));
            setLoading(false);
        } else if (signedInUser) {
            // Defensive only — see friendlyAuthError; Supabase errors rather
            // than returning a user for an unconfirmed account.
            if (!signedInUser.email_confirmed_at) {
                setError('Please verify your email address before signing in. Check your inbox for the verification link.');
                setLoading(false);
            } else {
                // Verify admin role
                const role = String(signedInUser.user_metadata?.role);
                if (role === '4') {
                    navigate('/admin');
                } else {
                    // Rejecting the role used to leave the session signed in, so
                    // a non-admin was authenticated despite being shown "Access
                    // denied" — and /sign-in would then bounce them into the
                    // user app. Drop the session we just created.
                    await signOut();
                    setError('Access denied. This login is for administrators only.');
                    setLoading(false);
                }
            }
        }
    };

    return (
        <main className="main-sectionWrapper admin-login-page">
            <div id="main-section" style={styles.mainSection}>
                {/* Split Screen Design */}
                <div style={styles.splitContainer}>
                    {/* Left Side - Branding & Info */}
                    <div className="split-left-panel" style={styles.leftPanel}>
                        <div style={styles.leftContent}>
                            {/* Logo and Branding */}
                            <div style={styles.brandSection}>
                                <div style={styles.logoWrapper}>
                                    <div style={styles.logoCircle}>
                                        <img src={sigmaLogo} alt="Sigma Bank" style={styles.logoImage} />
                                    </div>
                                </div>
                                <h2 style={styles.adminTitle}>Administrator Portal</h2>
                                <p style={styles.brandDescription}>
                                    Secure access to the administrative dashboard for wallet management,
                                    user oversight, and system configuration.
                                </p>
                            </div>

                            {/* Features List */}
                            <div style={styles.featuresList}>
                                <div style={styles.featureItem}>
                                    <div style={styles.featureIconWrapper}>
                                        <i className="bx bx-user-check"></i>
                                    </div>
                                    <div>
                                        <h3 style={styles.featureTitle}>User Management</h3>
                                        <p style={styles.featureDesc}>Manage all users and permissions</p>
                                    </div>
                                </div>
                                <div style={styles.featureItem}>
                                    <div style={styles.featureIconWrapper}>
                                        <i className="bx bx-wallet"></i>
                                    </div>
                                    <div>
                                        <h3 style={styles.featureTitle}>Wallet Analytics</h3>
                                        <p style={styles.featureDesc}>Monitor balances and transaction flows</p>
                                    </div>
                                </div>
                                <div style={styles.featureItem}>
                                    <div style={styles.featureIconWrapper}>
                                        <i className="bx bx-line-chart"></i>
                                    </div>
                                    <div>
                                        <h3 style={styles.featureTitle}>Analytics & Reports</h3>
                                        <p style={styles.featureDesc}>Access detailed system analytics</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Background Decoration */}
                        <div style={styles.leftDecoration}>
                            <div style={styles.decorOrb1}></div>
                            <div style={styles.decorOrb2}></div>
                            <div style={styles.decorOrb3}></div>
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className="split-right-panel" style={styles.rightPanel}>
                        <div style={styles.formContainer}>
                            {/* Welcome Section */}
                            <div style={styles.welcomeSection}>
                                <div style={styles.adminBadge}>
                                    <i className="bx bx-shield" style={styles.badgeIcon}></i>
                                    <span>Admin Access</span>
                                </div>
                                <h2 style={styles.welcomeTitle}>Welcome Back</h2>
                                <p style={styles.welcomeSubtitle}>
                                    Please enter your credentials to access the admin panel
                                </p>
                            </div>

                            {/* Login Form */}
                            <form onSubmit={handleSignIn} style={styles.form}>
                                {/* Email Field */}
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <i className="bx bx-envelope" style={styles.labelIcon}></i>
                                        Email Address
                                    </label>
                                    <input 
                                        type="email" 
                                        name="email" 
                                        placeholder="admin@sigmabank.com" 
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)} 
                                        disabled={loading}
                                        style={styles.input}
                                        autoComplete="email"
                                    />
                                </div>

                                {/* Password Field */}
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <i className="bx bx-lock-alt" style={styles.labelIcon}></i>
                                        Password
                                    </label>
                                    <div style={styles.passwordWrapper}>
                                        <input 
                                            type={showPassword ? 'text' : 'password'} 
                                            name="password" 
                                            placeholder="Enter your admin password" 
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)} 
                                            disabled={loading}
                                            style={styles.input}
                                            autoComplete="current-password"
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={styles.eyeButton}
                                            tabIndex="-1"
                                        >
                                            <i className={`bx ${showPassword ? 'bx-hide' : 'bx-show'}`}></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Remember Me & Forgot Password */}
                                <div style={styles.optionsRow}>
                                    <label style={styles.checkboxLabel}>
                                        <input type="checkbox" style={styles.checkbox} />
                                        <span style={styles.checkboxText}>Remember me</span>
                                    </label>
                                    <Link to="/forgot-password" style={styles.forgotLink}>
                                        Forgot password?
                                    </Link>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div style={styles.errorBox}>
                                        <i className="bx bx-error-circle" style={styles.errorIcon}></i>
                                        <span style={styles.errorText}>{error}</span>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <button 
                                    type="submit" 
                                    style={{
                                        ...styles.submitButton,
                                        opacity: loading ? 0.7 : 1,
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div style={styles.spinner}></div>
                                            <span>Signing In...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-log-in" style={styles.buttonIcon}></i>
                                            <span>Sign In to Dashboard</span>
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Footer Info */}
                            <div style={styles.formFooter}>
                                <p style={styles.footerText}>
                                    <i className="bx bx-info-circle" style={styles.footerIcon}></i>
                                    For security reasons, admin accounts cannot be created through this portal.
                                    Contact system administrator for access.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add CSS Animation Styles */}
            <style>{`
                /* Override mobile-only restriction for admin login page */
                .admin-login-page {
                    display: block !important;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes pulse {
                    0%, 100% { transform: scale(1); opacity: 0.8; }
                    50% { transform: scale(1.1); opacity: 1; }
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }

                /* Mobile Responsive - Hide left panel on small screens only */
                @media (max-width: 968px) {
                    .split-left-panel {
                        display: none !important;
                    }
                }
            `}</style>
        </main>
    );
}

const styles = {
    mainSection: {
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
    },
    splitContainer: {
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
    },
    
    // Left Panel Styles
    leftPanel: {
        flex: '0 0 45%',
        background: 'linear-gradient(135deg, #0e0758 0%, #1a0d4d 50%, #0e0758 100%)',
        position: 'relative',
        padding: '60px 50px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    leftContent: {
        position: 'relative',
        zIndex: 2,
        maxWidth: '500px',
        margin: '0 auto',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: 'rgba(255, 255, 255, 0.7)',
        textDecoration: 'none',
        fontSize: '14px',
        marginBottom: '40px',
        transition: 'all 0.3s ease',
    },
    backIcon: {
        fontSize: '20px',
    },
    brandSection: {
        textAlign: 'center',
        marginBottom: '50px',
    },
    logoWrapper: {
        position: 'relative',
        display: 'inline-block',
        marginBottom: '25px',
    },
    logoCircle: {
        width: '110px',
        height: '110px',
        borderRadius: '50%',
        background: '#FFFFFF',
        border: '2px solid rgba(25, 191, 177, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        zIndex: 1,
    },
    logoImage: {
        width: '65px',
        height: 'auto',
    },

    adminTitle: {
        fontSize: '22px',
        fontWeight: '600',
        color: '#19bfb1',
        margin: '0 0 20px 0',
        letterSpacing: '0.5px',
    },
    brandDescription: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        lineHeight: '1.6',
        margin: 0,
    },
    featuresList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        marginBottom: '40px',
    },
    featureItem: {
        display: 'flex',
        gap: '15px',
        alignItems: 'flex-start',
    },
    featureIconWrapper: {
        width: '45px',
        height: '45px',
        borderRadius: '12px',
        background: 'rgba(25, 191, 177, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        color: '#19bfb1',
        flexShrink: 0,
    },
    featureTitle: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#fff',
        margin: '0 0 5px 0',
    },
    featureDesc: {
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.6)',
        margin: 0,
        lineHeight: '1.4',
    },
    securityNotice: {
        display: 'flex',
        gap: '12px',
        padding: '15px',
        background: 'rgba(255, 193, 7, 0.1)',
        border: '1px solid rgba(255, 193, 7, 0.3)',
        borderRadius: '10px',
        alignItems: 'flex-start',
    },
    securityIcon: {
        fontSize: '20px',
        color: '#ffc107',
        flexShrink: 0,
        marginTop: '2px',
    },
    securityText: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.8)',
        lineHeight: '1.5',
        margin: 0,
    },
    leftDecoration: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        overflow: 'hidden',
    },
    decorOrb1: {
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25, 191, 177, 0.15) 0%, transparent 70%)',
        top: '-100px',
        right: '-100px',
        animation: 'float 8s ease-in-out infinite',
    },
    decorOrb2: {
        position: 'absolute',
        width: '250px',
        height: '250px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(14, 7, 88, 0.3) 0%, transparent 70%)',
        bottom: '-50px',
        left: '-50px',
        animation: 'float 10s ease-in-out infinite reverse',
    },
    decorOrb3: {
        position: 'absolute',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25, 191, 177, 0.1) 0%, transparent 70%)',
        top: '50%',
        left: '10%',
        animation: 'float 6s ease-in-out infinite',
    },

    // Right Panel Styles
    rightPanel: {
        flex: '1',
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
    },
    formContainer: {
        width: '100%',
        maxWidth: '480px',
    },
    welcomeSection: {
        textAlign: 'center',
        marginBottom: '35px',
    },
    adminBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        color: '#fff',
        padding: '8px 20px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600',
        marginBottom: '20px',
    },
    badgeIcon: {
        fontSize: '16px',
    },
    welcomeTitle: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1a1a1a',
        margin: '0 0 10px 0',
    },
    welcomeSubtitle: {
        fontSize: '15px',
        color: '#666',
        margin: 0,
        lineHeight: '1.5',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#333',
    },
    labelIcon: {
        fontSize: '18px',
        color: '#19bfb1',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '15px',
        color: '#333',
        background: '#f8f9fa',
        border: '2px solid #e9ecef',
        borderRadius: '10px',
        outline: 'none',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box',
    },
    passwordWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    eyeButton: {
        position: 'absolute',
        right: '12px',
        background: 'none',
        border: 'none',
        color: '#999',
        fontSize: '20px',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.2s ease',
    },
    optionsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '-5px',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
    },
    checkbox: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
    },
    checkboxText: {
        fontSize: '14px',
        color: '#666',
    },
    forgotLink: {
        fontSize: '14px',
        color: '#19bfb1',
        textDecoration: 'none',
        fontWeight: '500',
        transition: 'color 0.2s ease',
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: '#fff5f5',
        border: '1px solid #feb2b2',
        borderRadius: '10px',
        padding: '12px 15px',
    },
    errorIcon: {
        fontSize: '20px',
        color: '#dc3545',
        flexShrink: 0,
    },
    errorText: {
        fontSize: '14px',
        color: '#c53030',
        lineHeight: '1.5',
    },
    submitButton: {
        width: '100%',
        padding: '16px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#fff',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        border: 'none',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(25, 191, 177, 0.3)',
        transition: 'all 0.3s ease',
        marginTop: '10px',
    },
    buttonIcon: {
        fontSize: '20px',
    },
    spinner: {
        width: '18px',
        height: '18px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: '2px solid #fff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    formFooter: {
        marginTop: '30px',
        textAlign: 'center',
    },
    footerText: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        fontSize: '13px',
        color: '#666',
        lineHeight: '1.6',
        padding: '12px 15px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
    },
    footerIcon: {
        fontSize: '16px',
        color: '#19bfb1',
        flexShrink: 0,
        marginTop: '2px',
    },
};

export default AdminSignIn;
